import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { ToasterService } from "@shared/services/toaster.service";
import { FE_StickerPack } from "@shared/models/integration";
import { AdminStickersApiService } from "@shared/services/admin/admin-stickers-api-service";
import { AdminStickerPackPreviewComponent, StickerPackPreviewMoadlInstance } from "./preview/preview.component";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { MediaService } from "@shared/services/media.service";
import { SpinnerComponent } from "../../elements/spinner/spinner.component";
import { IboxComponent } from "../../elements/ibox/ibox.component";
import { FormsModule } from "@angular/forms";
import { LogService } from "@shared/services/log.service";

import { UiSwitchModule } from "ngx-ui-switch";

@Component({
    templateUrl: "./sticker-packs.component.html",
    styleUrls: ["./sticker-packs.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [SpinnerComponent, IboxComponent, FormsModule, UiSwitchModule, TranslatePipe]
})
export class AdminStickerPacksComponent implements OnInit {

    public isLoading = true;
    public isUpdating = false;
    public packs: FE_StickerPack[];
    public tgUrl: string;
    public isImporting = false;

    constructor(
        private readonly adminStickers: AdminStickersApiService,
        private readonly toaster: ToasterService,
        private readonly modal: NgbModal,
        private readonly media: MediaService,
        public translate: TranslateService,
        private readonly logService: LogService,
        private readonly cdr: ChangeDetectorRef
    ) {
        this.translate = translate;
    }

    public ngOnInit() {
        this.adminStickers.getAllPacks().then(packs => {
            this.packs = packs;
            this.isLoading = false;
            this.cdr.markForCheck();
        }).catch(err => {
            this.logService.error(err);
            this.translate.get('Failed to load sticker packs').subscribe((res: string) => {
                this.toaster.pop("error", res);
            });
        });
    }

    public toggleEnabled(pack: FE_StickerPack) {
        pack.isEnabled = !pack.isEnabled;
        this.isUpdating = true;
        this.adminStickers.togglePack(pack.id, pack.isEnabled).then(() => {
            this.isUpdating = false;
        }).catch(err => {
            this.logService.error(err);
            pack.isEnabled = !pack.isEnabled; // revert change
            this.isUpdating = false;
            this.translate.get('Error updating sticker pack').subscribe((res: string) => {
                this.toaster.pop("error", res);
            });
        });
    }

    public previewStickers(pack: FE_StickerPack) {
        for (const sticker of pack.stickers) {
            if (sticker.image.mimetype === 'image/tgs') {
                const mxc = sticker.thumbnail.mxc;
                this.media.getMediaContent(mxc).subscribe(data => {
                    sticker.animationOpts = {
                        animationData: data,
                        loop: false,
                        autoplay: false,
                    };
                });
            }
        }
        const modalRef = this.modal.open(AdminStickerPackPreviewComponent, { size: 'lg' });
        const previewInstance = modalRef.componentInstance as StickerPackPreviewMoadlInstance;

        previewInstance.pack = pack;
    }

    public startTelegramImport() {
        this.isImporting = true;
        this.adminStickers.importFromTelegram(this.tgUrl).then(pack => {
            this.isImporting = false;
            this.tgUrl = "";
            this.packs.push(pack);
            this.translate.get('Telegram sticker pack imported').subscribe((res: string) => {
                this.toaster.pop("success", res);
            });
        }).catch(err => {
            this.logService.error(err);
            this.isImporting = false;
            const messageKey = err.status === 409
                ? 'Sticker pack has already been imported'
                : 'Error importing sticker pack';
            this.translate.get(messageKey).subscribe((res: string) => {
                this.toaster.pop("error", res);
            });
        });
    }

    public removePack(pack: FE_StickerPack) {
        this.isUpdating = true;
        this.adminStickers.removePack(pack.id).then(() => {
            for (let i = 0; i < this.packs.length; ++i) {
                if (this.packs[i].id === pack.id) {
                    this.packs.splice(i, 1);
                    break;
                }
            }
            this.isUpdating = false;
            this.toaster.pop("success", "Sticker pack removed");
        }).catch(err => {
            this.logService.error(err);
            this.isUpdating = false;
            this.toaster.pop("error", "Error removing sticker pack");
        });
    }
}
